import { ANALITYCS_PAGES, AnalyticsPageName } from '@/shared/constants/analytics.constants'
import type { AvailableFlowRoutes } from '@/shared/types/flow.types'
import { InboundFlowRoutes, OutboundFlowRoutes } from '@/shared/types/router.types'
import { formatDate } from '@/shared/utils'
import { TravelDirection } from '@/shared/types/common.types'
import { useUserStore } from '@/shared/store'
import { isProxy, toRaw } from 'vue'
import { type Plan } from '@/shared/types/api.types'

export type AnalyticsCategory = Exclude<TravelDirection, TravelDirection.UNKNOWN>
type AnalyticsPlanType = 'single' | 'multiple'
type NewSPAddonName = 'extra_benefits' | 'one_year_policy'
type SPSection = 'inclusive_cover' | 'extra_benefit_cover'

export interface PurchaseEventParams {
  category: TravelDirection
  coupon?: string | null
  transaction_id: number
  policy_id: string
  num_of_travelers: number
  addons_price?: number
  total_price: number
  list_of_addons?: string[]
  number_of_addons?: number
  choosen_card: string | null
  countries: string[]
  flg_coins: boolean
  phone?: string
  email?: string
}

interface SwitchAddonsParams {
  addon_name: string
  quote_id: string
  addon_price: number
  state: boolean
  person_name?: string
  index?: string
  num_of_travelers?: string
  category?: TravelDirection
}

interface ShowPlansEventParams {
  item_name: string
  price: number
  schemecode: string
}

const CLICK_POLICY_WORDINGS_EVENT = 'click_policy_wordings'
const CHANGE_DATES_EVENT = 'change_dates'
const CHANGE_TRAVELERS_EVENT = 'change_travelers'
const CLICK_TO_PROCEED_EVENT = 'click_proceed'
const SHOW_PLANS_EVENT = 'view_item_list'
const SELECT_PLAN_EVENT = 'select_item'
const EXPAND_ON_EVENT = 'expand_on'
const LEARN_MORE_EVENT = 'learn_more'
const VIEW_POPUP_EVENT = 'view_popup'

export const formatAnalyticsDate = (date: Date, mask = 'dd.MM.yyyy') => formatDate(date, 'en', mask)

const getInsuredTravellersPage = (): AnalyticsPageName => {
  const userStore = useUserStore()
  if (userStore.activeInsuredTravelersSubPage === 'edit') {
    return AnalyticsPageName.UPDATE_INFORMATION
  }

  if (userStore.activeInsuredTravelersSubPage === 'add') {
    return AnalyticsPageName.ADD_PERSON
  }

  return AnalyticsPageName.INSURED_PEOPLE
}

export class GoogleAnalyticsService {
  readonly productLabel: string

  constructor(product: string) {
    this.productLabel = product
  }

  private send(event: string, payload: Record<string, unknown> = {}) {
    if (typeof window.gtag !== 'function') {
      throw new TypeError('[QIC-Analytics] GoogleAnalyticsService called but gtag is not defined')
    }

    const patchedPayload = {
      product: this.productLabel,
      ...payload,
    }

    const args: ['event', string, any?] = ['event', event]
    if (patchedPayload) {
      args.push(patchedPayload)
    }
    window.gtag(...args)

    console.info(`[QIC-Analytics] Event ${event} is sent by GoogleAnalyticsService`)
    console.info(`%c [QIC-Analytics] Event payload: `, 'background: #5927FF; color: #fff')
    console.info(JSON.stringify(patchedPayload, null, '\t'))
  }

  clickToCheckPrice(category: AnalyticsCategory, country: string[]) {
    this.send('click_to_check_price', {
      position: 'top',
      category,
      country,
    })
  }

  clickPolicyWordings<T>(category?: T, paramName: string = 'category') {
    this.send(CLICK_POLICY_WORDINGS_EVENT, {
      [paramName]: category,
    })
  }

  clickToCheckPriceLanding(category: AnalyticsCategory) {
    this.send('click_to_check_price', {
      position: 'bottom',
      category,
    })
  }

  clickWhatIsCovered(category: AnalyticsCategory) {
    this.send('what_is_covered', {
      position: 'bottom',
      category,
    })
  }

  sendToMainPage(route: AvailableFlowRoutes) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    let page = pageData.page
    const category = pageData.category

    if (page === AnalyticsPageName.INSURED_PEOPLE) {
      page = getInsuredTravellersPage()
    }

    this.send('to_main_page', {
      category,
      page,
    })
  }

  sendClickPrivacyPolicy(routeName: InboundFlowRoutes | OutboundFlowRoutes, place?: string) {
    const pageData = ANALITYCS_PAGES[routeName]
    if (!pageData) return

    const category = pageData.category
    let page = pageData.page

    if (page === AnalyticsPageName.INSURED_PEOPLE) {
      page = getInsuredTravellersPage()
    }

    this.send('click_privacy_policy', {
      category,
      page,
      place,
    })
  }

  sendSelectCountryProceed(country: string) {
    this.send(CLICK_TO_PROCEED_EVENT, {
      category: TravelDirection.INBOUND,
      page: AnalyticsPageName.SELECT_COUNTRY,
      country: [country],
    })
  }

  sendTravelDatesProceed(
    category: AnalyticsCategory,
    tripType: 'single' | 'multi',
    departureDate: Date,
    returnDate: Date,
    countries: string[]
  ) {
    if (category === TravelDirection.OUTBOUND) {
      return this.send(CLICK_TO_PROCEED_EVENT, {
        category,
        page: ANALITYCS_PAGES[OutboundFlowRoutes.DATES]?.page,
        trip_type: tripType,
        departure: formatAnalyticsDate(departureDate),
        return: formatAnalyticsDate(returnDate),
        countries,
      })
    }
    this.send(CLICK_TO_PROCEED_EVENT, {
      category,
      page: ANALITYCS_PAGES[OutboundFlowRoutes.DATES]?.page,
      departure: formatAnalyticsDate(departureDate),
      return: formatAnalyticsDate(returnDate),
    })
  }

  sendNumberOfTravelersProceed(
    category: AnalyticsCategory,
    adults: number,
    children: number,
    countries: string[]
  ) {
    let countryList: string[] | undefined
    if (category === TravelDirection.OUTBOUND) {
      countryList = countries
    }
    this.send(CLICK_TO_PROCEED_EVENT, {
      category,
      page: AnalyticsPageName.TRAVELERS,
      value: adults + children,
      adults,
      children,
      countries: countryList,
    })
  }

  sendContactsProceed(
    category: AnalyticsCategory,
    promocode: string,
    isSubscribe: boolean,
    countries: string[]
  ) {
    let promo_code: string | undefined
    let countryList: string[] | undefined
    if (promocode) {
      promo_code = promocode
    }

    if (category === TravelDirection.OUTBOUND) {
      countryList = countries
    }

    this.send(CLICK_TO_PROCEED_EVENT, {
      category,
      page: ANALITYCS_PAGES[InboundFlowRoutes.CONTACTS]?.page,
      subscribed: isSubscribe ? 'Yes' : 'No',
      promo_code,
      country: countryList,
    })
  }

  sendSelectPlanProceed(
    price: number,
    numTravelers: number,
    departureDate: Date,
    returnDate: Date
  ) {
    this.send(CLICK_TO_PROCEED_EVENT, {
      category: TravelDirection.INBOUND,
      page: AnalyticsPageName.SELECT_PLAN,
      price: String(price),
      num_travelers: numTravelers,
      departure_date: formatAnalyticsDate(departureDate),
      return_date: formatAnalyticsDate(returnDate),
    })
  }

  sendOutboundSelectPlanProceed(
    isSingle: boolean,
    plan: Plan,
    countries: string[],
    numOfTravelers: string,
    discountPrice?: number
  ) {
    this.send(SELECT_PLAN_EVENT, {
      category: TravelDirection.OUTBOUND,
      plan_type: isSingle ? 'single' : 'multiple',
      page: AnalyticsPageName.SELECT_PLAN,
      plan_name: plan.name,
      schemeCode: plan.schemecode,
      price: String(plan.price),
      countries,
      num_of_travelers: numOfTravelers,
      discount_price: discountPrice,
    })
  }

  sendOutboundNewSPSelect(data: {
    planType: AnalyticsPlanType
    planName: string
    price: number
    countries: string[]
    numOfTravelers: number
    switchesEnabled: NewSPAddonName[]
    numOfAddons: number
  }) {
    this.send(SELECT_PLAN_EVENT, {
      category: TravelDirection.OUTBOUND,
      planType: data.planType,
      page: AnalyticsPageName.SELECT_PLAN,
      plan_name: data.planName,
      price: data.price,
      countries: data.countries,
      num_of_travelers: data.numOfTravelers,
      switches_enabled: data.switchesEnabled,
      number_of_addons: data.numOfAddons,
    })
  }

  sendSelectPlanChangeDate(price: number, departureDate: Date, returnDate: Date) {
    this.send(CHANGE_DATES_EVENT, {
      category: TravelDirection.INBOUND,
      page: AnalyticsPageName.SELECT_PLAN,
      price: String(price),
      departure_date: formatAnalyticsDate(departureDate),
      return_date: formatAnalyticsDate(returnDate),
    })
  }

  sendOutboundSelectPlanChangeDate(departureDate: Date, returnDate: Date) {
    this.send(CHANGE_DATES_EVENT, {
      category: TravelDirection.OUTBOUND,
      page: AnalyticsPageName.SELECT_PLAN,
      departure: formatAnalyticsDate(departureDate),
      return: formatAnalyticsDate(returnDate),
    })
  }

  sendSelectPlanChangeTravelers(price: number, numTravelers: number) {
    this.send(CHANGE_TRAVELERS_EVENT, {
      category: TravelDirection.INBOUND,
      page: AnalyticsPageName.SELECT_PLAN,
      price: String(price),
      num_travelers: numTravelers,
    })
  }

  sendOutboundSelectPlanChangeTravelers(adults: number, children: number) {
    this.send(CHANGE_TRAVELERS_EVENT, {
      category: TravelDirection.OUTBOUND,
      page: AnalyticsPageName.SELECT_PLAN,
      num_travelers: adults + children,
    })
  }

  sendSelectPlanPolicyWordings(price: number) {
    this.send(CLICK_POLICY_WORDINGS_EVENT, {
      category: TravelDirection.INBOUND,
      page: AnalyticsPageName.SELECT_PLAN,
      price: String(price),
    })
  }

  sendOutboundSelectPlanPolicyWordings(name: string, price: number) {
    this.send(CLICK_POLICY_WORDINGS_EVENT, {
      category: TravelDirection.OUTBOUND,
      page: AnalyticsPageName.SELECT_PLAN,
      plan_name: name,
      price: String(price),
    })
  }

  sendOutboundPlanDetailsPolicyWordings() {
    this.send(CLICK_POLICY_WORDINGS_EVENT, {
      page: AnalyticsPageName.SELECT_PLAN,
      popup_name: 'plan_details',
    })
  }

  sendOutboundSelectPlanDestinationChange(countries: string[]) {
    this.send('change_destinations', {
      category: TravelDirection.OUTBOUND,
      page: AnalyticsPageName.SELECT_PLAN,
      countries,
    })
  }

  sendOutboundSelectPlanShowPlans(plansGA: ShowPlansEventParams[]) {
    this.send(SHOW_PLANS_EVENT, {
      category: TravelDirection.OUTBOUND,
      page: AnalyticsPageName.SELECT_PLAN,
      items: plansGA,
      number_of_plans: plansGA.length,
    })
  }

  sendOutboundSelectPlanPromo(title: string) {
    const page = window.location.pathname.split('/').filter(Boolean).pop() || ''
    this.send('click_link', {
      product: 'travel',
      categoty: 'outbound',
      promo_name: 'ramadan',
      page: page === 'quote' ? 'quote' : AnalyticsPageName.SELECT_PLAN,
      link_title: title,
    })
  }

  sendPersonalInformationProceed(
    category: AnalyticsCategory,
    nationality: string,
    countries: string[],
    birthdate: Date,
    gender: string
  ) {
    let countryList: string[] | undefined
    if (category === TravelDirection.OUTBOUND) {
      countryList = countries
    }

    this.send(CLICK_TO_PROCEED_EVENT, {
      category,
      page: AnalyticsPageName.HOLDER_INFO,
      nationality,
      countries: countryList,
      dob: formatAnalyticsDate(birthdate),
      gender,
    })
  }

  sendInsuredTravelersProceed(
    category: AnalyticsCategory,
    numTravelers: number,
    countries: string[]
  ) {
    let countryList: string[] | undefined
    if (category === TravelDirection.OUTBOUND) {
      countryList = countries
    }
    this.send(CLICK_TO_PROCEED_EVENT, {
      category,
      page: AnalyticsPageName.INSURED_PEOPLE,
      num_travelers: numTravelers,
      countries: countryList,
    })
  }

  sendInsuredTravelersEdit(category: AnalyticsCategory) {
    this.send('edit_person', {
      category,
      page: AnalyticsPageName.INSURED_PEOPLE,
    })
  }

  sendInsuredTravelersAdd(category: AnalyticsCategory) {
    this.send('add_person', {
      category,
      page: AnalyticsPageName.INSURED_PEOPLE,
    })
  }

  sendInsuredTravelersAddNew(
    category: AnalyticsCategory,
    relationship: string,
    nationality: string
  ) {
    this.send('added_new_person', {
      category,
      page: AnalyticsPageName.ADD_PERSON,
      relationship,
      nationality,
    })
  }

  sendInsuredTravelersDelete(category: AnalyticsCategory) {
    this.send('delete_person', {
      category,
      page: AnalyticsPageName.INSURED_PEOPLE,
    })
  }

  sendInsuredTravelersUpdateProceed(
    category: AnalyticsCategory,
    nationality: string,
    relationName?: string
  ) {
    let relationship: string | undefined

    if (relationName) {
      relationship = relationName
    }

    this.send(CLICK_TO_PROCEED_EVENT, {
      category,
      page: AnalyticsPageName.UPDATE_INFORMATION,
      nationality,
      relationship,
    })
  }

  sendDestinationProceed(countries: string[]) {
    this.send(CLICK_TO_PROCEED_EVENT, {
      category: TravelDirection.OUTBOUND,
      page: AnalyticsPageName.DESTINATION,
      countries,
    })
  }

  sendQuoteBuy(
    coupon: string,
    transactionId: number,
    numTravelers: number,
    totalPrice: number,
    countries: string[],
    choosenCard: string
  ) {
    this.send('start_purchase', {
      category: TravelDirection.INBOUND,
      page: AnalyticsPageName.QUOTE,
      coupon,
      transaction_id: transactionId,
      num_travelers: numTravelers,
      total_price: String(totalPrice),
      countries,
      choosen_card: choosenCard,
    })
  }

  sendOutboundQuoteBuy(
    coupon: string,
    transactionId: number,
    numTravelers: number,
    addonsPrice: number,
    totalPrice: number,
    listOfAddons: string[],
    countries: string[],
    choosenCard: string,
    number_of_addons: number,
    tabTitle?: 'earn' | 'spend',
    discountPrice?: number
  ) {
    this.send('start_purchase', {
      category: TravelDirection.OUTBOUND,
      page: AnalyticsPageName.QUOTE,
      coupon,
      transaction_id: transactionId,
      num_of_travelers: numTravelers,
      addons_price: String(addonsPrice),
      total_price: String(totalPrice),
      // TODO: Hack. Try to resolve.
      switches_enabled: isProxy(listOfAddons) ? toRaw(listOfAddons) : listOfAddons,
      countries,
      choosen_card: choosenCard,
      number_of_addons,
      tab_title: tabTitle,
      discount_price: discountPrice,
    })
  }

  sendDownloadPolicyEvent(product: string, transactionId: string | number) {
    this.send('view_page', {
      product,
      page: AnalyticsPageName.DOWNLOAD_POLICY,
      transaction_id: transactionId,
    })
  }

  sendPurchaseEvent(params: PurchaseEventParams) {
    this.send('purchase', {
      ...params,
      coupon: params.coupon || '',
      choosen_card: params.choosen_card || '',
      page: AnalyticsPageName.QUOTE,
    })
  }

  sendToVisitors() {
    this.send('to_visitors', {
      category: TravelDirection.INBOUND,
      page: ANALITYCS_PAGES[InboundFlowRoutes.RESIDENCE]?.page,
    })
  }

  sendContactUsPopup(route: AvailableFlowRoutes) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const page = pageData.page
    const category = pageData.category

    this.send('contact_us', {
      category,
      page,
    })
  }

  sendTermsConditions(route: AvailableFlowRoutes, quote_id: string) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const page = pageData.page
    const category = pageData.category

    this.send('click_terms_conditions', {
      category,
      page,
      quote_id,
    })
  }

  sendPaymentTermsConditions(route: AvailableFlowRoutes, quote_id: string) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const page = pageData.page
    const category = pageData.category

    this.send('click_payment_terms_conditions', {
      category,
      page,
      quote_id,
    })
  }

  sendClickPhone(route: AvailableFlowRoutes, phone: string) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const page = pageData.page
    const category = pageData.category

    this.send('click_send_phone', {
      category,
      page,
      phone,
      place: 'popup_contact_us',
    })
  }

  sendWhatsApp(route: AvailableFlowRoutes) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const page = pageData.page
    const category = pageData.category

    this.send('click_whatsapp', {
      category,
      page,
      place: 'popup_contact_us',
    })
  }

  sendDownloadDoc(event: string, category: string, transaction_id: string | number) {
    this.send(event, {
      category,
      page: AnalyticsPageName.DOWNLOAD_POLICY,
      policy_id: transaction_id,
    })
  }

  sendPolicyDownload(policyId: string, category: TravelDirection, btnTitle: string) {
    this.send('click_btn', {
      category,
      page: AnalyticsPageName.DOWNLOAD_POLICY,
      policy_id: policyId,
      btn_title: btnTitle,
    })
  }

  sendAbTest(group: string, value: string) {
    this.send('exp_get', { group, value })
  }

  sendViewQuote(
    route: AvailableFlowRoutes,
    countries: string[],
    listOfAddons: string[],
    addons: number,
    numOfTravelers: string,
    addonsPrice: number,
    quote_id: string,
    totalPrice: number
  ) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const page = pageData.page
    const category = pageData.category
    this.send('view_quote', {
      category,
      page,
      countries,
      switches_enabled: isProxy(listOfAddons) ? toRaw(listOfAddons) : listOfAddons,
      number_of_addons: addons,
      num_of_travelers: numOfTravelers,
      addons_price: String(addonsPrice),
      quote_id,
      total_price: String(totalPrice),
    })
  }

  sendSwitchAddon(params: SwitchAddonsParams) {
    this.send('switch_addon', {
      ...params,
      category: params.category || TravelDirection.OUTBOUND,
      page: AnalyticsPageName.QUOTE,
    })
  }

  sendQdAppDownload(policyId: string) {
    this.send('download_app', {
      category: TravelDirection.OUTBOUND,
      page: AnalyticsPageName.DOWNLOAD_POLICY,
      policy_id: policyId,
    })
  }

  sendAppDownloadPromo(policyId: string) {
    this.send('download_app', {
      product: 'travel',
      category: 'outbound',
      page: AnalyticsPageName.DOWNLOAD_POLICY,
      promo_name: 'ramadan',
      policy_id: policyId,
    })
  }

  sendPersonalInformationFieldCompleted(field: string, position: number) {
    this.send('field_completed', {
      category: TravelDirection.OUTBOUND,
      page: 'holder-info',
      field_name: field,
      field_position: position,
    })
  }

  sendQidFieldCompleted(birthdate?: Date, nationality?: string) {
    this.send('field_completed', {
      category: TravelDirection.OUTBOUND,
      page: 'holder-info',
      dob: birthdate ? formatAnalyticsDate(birthdate) : null,
      country_of_residense: nationality || null,
      field_name: 'qid',
    })
  }

  sendNewSPSwitchAddon(data: {
    planType: AnalyticsPlanType
    planName: string
    state: boolean
    totalPrice: number
    addonName: NewSPAddonName
    addonPrice: number
  }) {
    this.send('switch_addon', {
      category: TravelDirection.OUTBOUND,
      page: AnalyticsPageName.SELECT_PLAN,
      plan_type: data.planType,
      plan_name: data.planName,
      state: data.state,
      total_price: data.totalPrice,
      addon_name: data.addonName,
      addon_price: data.addonPrice,
    })
  }

  sendCoverToggle(data: {
    category: AnalyticsCategory
    planName: string
    planPrice: number
    coverName: string
    section: SPSection
    numOfTravelers: number
  }) {
    this.send('expand_on', {
      page: AnalyticsPageName.SELECT_PLAN,
      category: data.category,
      section: data.section,
      num_of_travelers: data.numOfTravelers,
      cover_name: data.coverName,
      plan_name: data.planName,
      price: data.planPrice,
    })
  }

  sendCoverToggleQuote(data: {
    category: AnalyticsCategory
    quoteId: number
    totalPrice: number
    coverName: string
    numOfTravelers: number
    section?: string
  }) {
    this.send(EXPAND_ON_EVENT, {
      page: AnalyticsPageName.QUOTE,
      category: data.category,
      section: data.section ?? 'inclusive_cover_info',
      num_of_travelers: data.numOfTravelers,
      cover_name: data.coverName,
      quote_id: data.quoteId,
      total_price: data.totalPrice,
    })
  }

  sendTravellersToggleQuote(data: {
    category: AnalyticsCategory
    quoteId: number
    totalPrice: number
    numOfTravelers: number
  }) {
    this.send(EXPAND_ON_EVENT, {
      page: AnalyticsPageName.QUOTE,
      category: data.category,
      section: 'expand_list_of_travellers',
      num_of_travelers: data.numOfTravelers,
      quote_id: data.quoteId,
      total_price: data.totalPrice,
    })
  }

  sendEditTravellersQuote(data: {
    category: AnalyticsCategory
    countries: string[]
    quoteId: number
    totalPrice: number
    numOfTravelers: number
  }) {
    this.send('edit_person', {
      page: AnalyticsPageName.QUOTE,
      category: data.category,
      countries: data.countries,
      num_of_travelers: data.numOfTravelers,
      quote_id: data.quoteId,
      total_price: data.totalPrice,
    })
  }

  sendCyberTooltip(category: AnalyticsCategory, page: string, text: string, quote_id?: number) {
    this.send('click_icon', {
      category,
      page,
      text,
      quote_id,
    })
  }

  sendCyberPopupOpened(route: AvailableFlowRoutes, quote_id?: number) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const payload: Record<string, string | number> = {
      page: pageData.page,
      label: 'cyber_security',
      ...(pageData.page === AnalyticsPageName.QUOTE && { quote_id }),
    }

    this.send(VIEW_POPUP_EVENT, payload)
  }

  sendPlanDetailsPopupOpened(route: AvailableFlowRoutes) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const payload: Record<string, string | number> = {
      page: pageData.page,
      popup_name: 'plan_details',
    }

    this.send(VIEW_POPUP_EVENT, payload)
  }

  sendPlanTabClick(route: AvailableFlowRoutes, tab: string) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const payload: Record<string, string | number> = {
      page: pageData.page,
      popup_name: 'plan_details',
      click_btn: tab,
    }

    this.send('click_btn', payload)
  }

  sendAddCyberToCoverage(route: AvailableFlowRoutes, quote_id: number) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const payload: Record<string, string | number> = {
      page: pageData.page,
      label: 'cyber_security',
      quote_id,
      click_btn: 'add_to_main_coverage',
    }

    this.send('click_btn', payload)
  }

  sendClickLearnMore(
    route: AvailableFlowRoutes,
    options?: {
      quote_id?: number
      state?: boolean
      section?: string
      cover_name?: string
    }
  ) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const { quote_id, state, section, cover_name } = options || {}

    const payload: Record<string, string | number | boolean> = {
      page: pageData.page,
      category: pageData.category,
      ...(cover_name !== undefined && { cover_name }),
      ...(pageData.page === AnalyticsPageName.QUOTE && { quote_id }),
      ...(pageData.page === AnalyticsPageName.QUOTE && { state }),
      ...(section !== undefined && { section }),
    }

    this.send(LEARN_MORE_EVENT, payload)
  }

  sendGoToStore(policyId: string, category: TravelDirection) {
    this.send('click_btn', {
      plan_name: category,
      page: AnalyticsPageName.SUCCESS_PAGE,
      policy_id: policyId,
      btn_title: 'go_to_store',
    })
  }

  sendSeeEarnedCoins(policyId: string, category: TravelDirection, bonusesAmount: number) {
    this.send('click_btn', {
      plan_name: category,
      page: AnalyticsPageName.SUCCESS_PAGE,
      policy_id: policyId,
      btn_title: 'see_more',
      bonus_value: bonusesAmount,
    })
  }

  sendWebViewApp(googleId: string) {
    this.send('come_from_app', {
      google_id: googleId,
    })
  }

  sendHomeDiscountBanner(policyId: string) {
    this.send('click_btn', {
      policy_id: policyId,
      page: AnalyticsPageName.DOWNLOAD_POLICY,
      btn_title: 'use_your_discount',
      section: 'banner',
      banner_title: 'home_discount',
    })
  }

  sendCoinsRewardTabClick(
    option: 'earn' | 'spend',
    category: TravelDirection,
    numTravelers: number,
    quoteId: number,
    totalPrice: number,
    discountPrice?: number
  ) {
    this.send('click_tab', {
      page: AnalyticsPageName.QUOTE,
      category,
      tab_title: option,
      num_of_travelers: numTravelers,
      quote_id: quoteId,
      total_price: totalPrice,
      discount_price: discountPrice,
    })
  }

  sendClickOoredooBanner(policyId: string) {
    this.send('click_btn', {
      policy_id: policyId,
      page: AnalyticsPageName.DOWNLOAD_POLICY,
      btn_title: 'claim_free_esim',
      section: 'banner',
      banner_title: 'ooredoo_banner',
    })
  }

  sendViewHotelBanner(policyId: string) {
    this.send('view_banner', {
      policy_id: policyId,
      page: AnalyticsPageName.DOWNLOAD_POLICY,
      banner_title: 'hotel_banner',
    })
  }

  sendClickChooseHotel(policyId: string) {
    this.send('click_btn', {
      policy_id: policyId,
      page: AnalyticsPageName.DOWNLOAD_POLICY,
      btn_title: 'choose_hotel',
      section: 'banner',
      banner_title: 'hotel_banner',
    })
  }

  sendClickSelectPlanLearnMore() {
    this.send('click_learn_more', {
      page: AnalyticsPageName.SELECT_PLAN,
      section: 'plans_block',
      category: 'outbound',
    })
  }

  sendClickSelectPlanWhatsCovered() {
    this.send('what_is_covered', {
      page: AnalyticsPageName.SELECT_PLAN,
      category: 'outbound',
    })
  }
  sendClickSelectPlanBackToPlans() {
    this.send('click_btn', {
      page: AnalyticsPageName.SELECT_PLAN,
      category: 'outbound',
      btn_title: 'back_to_plans',
    })
  }

  sendViewPage(category: AnalyticsCategory, page: AnalyticsPageName) {
    this.send('view_page', { category, page })
  }
}
